/**
 * Local file viewer store. Mirrors the artifact-store mount pattern so the
 * viewer slides in as a sibling to the chat motion.div, but stays decoupled
 * — the artifact store is server-backed (auto-save, /api/artifacts/:id),
 * while this one is a read-only window onto a `FileSystemFileHandle`.
 *
 * @module file-agent/store/local-file-viewer-store
 */

import { create } from "zustand";

/** Categories the viewer renders inline. Anything else falls through to the external/fallback card. */
export type LocalFileKind =
  | "markdown"
  | "text"
  | "image"
  | "csv"
  | "html"
  | "external";

export interface LocalFile {
  /** File name, e.g. "PetroDollar.md". */
  name: string;
  /** Path relative to the attached folder root, e.g. "Briefs/PetroDollar.md". */
  path: string;
  /** Inferred from extension; drives which inline renderer the viewer uses. */
  kind: LocalFileKind;
  /** Live handle to read bytes/text from. */
  handle: FileSystemFileHandle;
}

interface LocalFileViewerStore {
  visible: boolean;
  file: LocalFile | null;
  open: (file: LocalFile) => void;
  close: () => void;
}

/**
 * Map a file name to its viewer kind. Centralized so the tree panel and
 * the viewer agree on which renderer will mount.
 */
export function detectLocalFileKind(name: string): LocalFileKind {
  const ext = name.toLowerCase().split(".").pop() ?? "";
  switch (ext) {
    case "md":
    case "markdown":
    case "mdx":
      return "markdown";
    case "txt":
    case "log":
      return "text";
    case "csv":
      return "csv";
    case "html":
    case "htm":
      return "html";
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
    case "svg":
    case "bmp":
      return "image";
    default:
      return "external";
  }
}

export const useLocalFileViewerStore = create<LocalFileViewerStore>((set) => ({
  visible: false,
  file: null,
  open: (file) => set({ visible: true, file }),
  close: () => set({ visible: false }),
}));
